import { z } from 'zod';
import { zfd } from 'zod-form-data';
import api from '~/utils/api';
import { ClaimType, ResolutionMethod } from '~/utils/constants';
import {
  makeDraftClaimLineItemWithReasonAction,
  makeDraftClaimLineItemWithReasonLoader,
  redirect,
} from '~/utils/routing';
import {
  advancedExchangeMethodSchema,
  claimLineItemSchema,
  exchangeVariantSchema,
} from '~/utils/schemas';

export const { loader, useLoaderData } = makeDraftClaimLineItemWithReasonLoader(
  ({ context }, appStore) => {
    const {
      draftClaimLineItem,
      crewClaim: { claimType },
      settings: { storeSlug: store },
      order: { idFromPlatform, offerShopNow },
    } = context;

    if (claimType === ClaimType.warranty) {
      const parsedClaim = claimLineItemSchema.parse({
        ...draftClaimLineItem,
        requestedResolutionMethodEnum: ResolutionMethod.warrantyReview,
      });

      appStore.dispatch(
        appStore.actions.completeDraftClaimLineItem(parsedClaim),
      );

      return redirect(
        `/${store}/order/${idFromPlatform}/${claimType.toLowerCase()}`,
      );
    }

    const { storefrontId } = context.settings;
    const { shouldPrioritizeCredit, creditDetailText, creditHeadlineText } =
      context.settings.theme;

    const { idFromPlatform: orderIdFromPlatform } = context.order;
    const { isRefundEnabled, isGiftCardEnabled, isExchangeEnabled } =
      context.settings.return;

    const {
      return: {
        isExchangeOnly,
        // isEligible - as in within the time window
        exchange: { isEligible: isExchangeEligible },
        refund: { isEligible: isRefundEligible },
        giftCard: { isEligible: isGiftCardEligible },
      },
      productIdFromPlatform,
    } = draftClaimLineItem.lineItem;

    const canExchange = isExchangeEnabled && isExchangeEligible;
    // ? does offerShopNow impact ability to credit?
    const canCredit =
      ((isRefundEnabled && isRefundEligible) ||
        (isGiftCardEnabled && isGiftCardEligible)) &&
      !isExchangeOnly;

    const canGiftCard = isGiftCardEligible && isGiftCardEnabled;

    const exchangeData =
      canExchange && productIdFromPlatform ?
        api
          .fetchProductVariantInfo({
            params: {
              storefrontId,
              idFromPlatform: productIdFromPlatform,
            },
            query: {
              isExchangeOfExchange: !!context.order.isExchangeOfExchange,
            },
          })
          .then((results) => ({
            ...results,
            products: results.products.map((result) => ({
              ...result,
              options: result.options?.sort((a, b) => a.position - b.position),
            })),
          }))
      : Promise.resolve({
          advancedExchangeMethod: 'NO_PAYMENT_OR_REFUND' as const,
          products: [],
          filters: [],
          isFilterAndSearchEnabled: false,
        });

    return exchangeData
      .then((data) => ({
        exchangeProducts: data.products,
        exchangeFilters: data.filters ?? [],
        isFilterAndSearchEnabled: data.isFilterAndSearchEnabled ?? false,

        draftClaimLineItem,
        advancedExchangeMethod: data.advancedExchangeMethod,
        canExchange: canExchange && data.products.length > 0,
        orderId: orderIdFromPlatform,
        offerShopNow: !!offerShopNow,
        store,
        shouldPrioritizeCredit: !!shouldPrioritizeCredit,
        creditHeadlineText: creditHeadlineText ?? 'Not looking to exchange?',
        creditDetailText:
          creditDetailText ?? 'Please continue to finish your request.',
        canCredit,
        ...(canCredit && {
          creditResolutionMethod:
            canGiftCard ? ResolutionMethod.giftCard : ResolutionMethod.refund,
        }),
      }))
      .catch((err: unknown) =>
        err instanceof Error ? err : (
          new Error('Unknown error', { cause: { originalError: err } })
        ),
      );
  },
);

export enum FormKey {
  resolution = 'resolutionMethod',
  variant = 'variant',
  advancedExchangeMethod = 'advancedExchangeMethod',
  shopNow = 'shopNow',
}

const formSchema = zfd.formData(
  z.discriminatedUnion(FormKey.resolution, [
    z.object({
      [FormKey.resolution]: zfd.text(
        z.literal(ResolutionMethod.variantExchange),
      ),
      [FormKey.advancedExchangeMethod]: zfd.text(advancedExchangeMethodSchema),
      [FormKey.variant]: zfd.json(exchangeVariantSchema),
      [FormKey.shopNow]: z.never().optional(),
    }),
    z.object({
      [FormKey.resolution]: zfd.text(
        z.enum([ResolutionMethod.refund, ResolutionMethod.giftCard]),
      ),
      [FormKey.variant]: zfd.text(z.never().optional()),
      [FormKey.advancedExchangeMethod]: zfd.text(z.never().optional()),
      // treating shopNow as an array since it is a value on a button and iOS does some weird stuff
      // where it sends the value twice with the same name
      [FormKey.shopNow]: zfd
        .repeatableOfType(zfd.checkbox({ trueValue: 'true' }).optional())
        .optional(),
    }),
  ]),
);

export const { action, useActionResult } =
  makeDraftClaimLineItemWithReasonAction(
    ({ context, formData, params: { store } }, appStore) => {
      const {
        draftClaimLineItem,
        crewClaim: { claimType },
      } = context;
      const form = formSchema.safeParse(formData);

      if (!form.success) {
        return new Error('Malformed form data', {
          cause: {
            error: form.error,
            entries: Array.from(formData.entries()),
          },
        });
      }

      const parsedClaim = claimLineItemSchema.parse({
        ...draftClaimLineItem,
        shopNow: form.data[FormKey.shopNow]?.every((v) => v) ?? false,
        requestedResolutionMethodEnum: form.data[FormKey.resolution],
        ...(form.data[FormKey.resolution] === ResolutionMethod.variantExchange ?
          {
            variant: form.data[FormKey.variant],
            grams: form.data[FormKey.variant].grams,
            advancedExchangeMethod: form.data[FormKey.advancedExchangeMethod],
          }
          // cover the case where user changes resolution method
        : {
            variant: undefined,
            variantExchangeLineItem: undefined,
            advancedExchangeMethod: undefined,
            grams: draftClaimLineItem.lineItem.grams,
          }),
      });

      appStore.dispatch(
        appStore.actions.completeDraftClaimLineItem(parsedClaim),
      );

      return redirect(
        `/${store}/order/${context.order.idFromPlatform}/${claimType.toLowerCase()}`,
      );
    },
  );
